/**
 * Version Sync — detects stale tenant config and user permissions via
 * response headers (X-Tenant-Version, X-User-Version) and triggers
 * silent background re-fetches so users don't need to log out.
 */

type VersionChangeCallback = () => void;

let knownTenantVersion: string | null = null;
let knownUserVersion: string | null = null;

let onTenantChanged: VersionChangeCallback | null = null;
let onUserChanged: VersionChangeCallback | null = null;

// Debounce flags to prevent multiple concurrent re-fetches
let tenantRefreshPending = false;
let userRefreshPending = false;

/**
 * Register callbacks that fire when the backend reports a version change.
 * Each callback is expected to silently re-fetch the relevant data.
 */
export function registerVersionCallbacks(callbacks: {
  onTenantChanged: VersionChangeCallback;
  onUserChanged: VersionChangeCallback;
}): void {
  onTenantChanged = callbacks.onTenantChanged;
  onUserChanged = callbacks.onUserChanged;
}

/**
 * Call this from the Axios response interceptor on every successful response.
 * Compares the incoming version headers against the last known values and
 * triggers the appropriate callback when a mismatch is detected.
 */
export function checkVersionHeaders(headers: Record<string, string>): void {
  const tenantVersion = headers["x-tenant-version"];
  const userVersion = headers["x-user-version"];

  if (tenantVersion) {
    if (knownTenantVersion !== null && knownTenantVersion !== tenantVersion) {
      if (!tenantRefreshPending && onTenantChanged) {
        tenantRefreshPending = true;
        console.log("[VersionSync] Tenant config changed, triggering refresh");
        try {
          onTenantChanged();
        } finally {
          // Allow future refreshes after a short cooldown
          setTimeout(() => {
            tenantRefreshPending = false;
          }, 5000);
        }
      }
    }
    knownTenantVersion = tenantVersion;
  }

  if (userVersion) {
    if (knownUserVersion !== null && knownUserVersion !== userVersion) {
      if (!userRefreshPending && onUserChanged) {
        userRefreshPending = true;
        console.log("[VersionSync] User permissions changed, triggering refresh");
        try {
          onUserChanged();
        } finally {
          setTimeout(() => {
            userRefreshPending = false;
          }, 5000);
        }
      }
    }
    knownUserVersion = userVersion;
  }
}

/**
 * Reset stored versions (call on logout or tenant switch).
 */
export function resetVersionState(): void {
  knownTenantVersion = null;
  knownUserVersion = null;
  tenantRefreshPending = false;
  userRefreshPending = false;
}
